import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import ActionLogger from "action-logger";
import ClosableModal from "../shared/closable-modal";
import { browserPropType } from "components/propTypes";
import FitChart from "./fit-chart";
import ProTeaser from "./pro-teaser";
import PDPSubmitActionsWrapper from "components/source/reservations/pdp-submit-actions-wrapper";
import SizeSelectors from "components/source/reservations/size-selectors";
import DateZipInput from "components/source/reservations/date-zip-input";
import ProductCollectionHelpers from "helpers/product-collection-helpers";
import { isBulk, isClearance, isRental } from "helpers/price-helper";
import AtomLinkButton from "components/source/atoms/atom-link-button";
import { merchandiseCategories, analytics, PDP_TYPE, membershipLens } from "rtr-constants";
import FitGuarantee from "components/source/shared/fit-guarantee/fit-guarantee";
import MembershipHelpers from "helpers/membership-helpers";
import KiffrAtbButton from "components/source/pdp/kiffr-atb-button";
import {
  flagsAndExperimentsPropType,
  flagsAndExperimentNames,
  withOnLoadFlagsAndExperiments,
} from "components/source/hoc/with-flags-and-experiments";
import KeepItForeverReturnsModal from "components/source/pdp/keep-it-forever-returns-modal";
import { getUserId } from "components/source/hoc/with-user-data";
import HeapHelpers from "helpers/heap-helpers";
import * as ProductsActions from "actions/products-actions";
import UrgencySignalWrapper from "../products/urgency-signal/wrapper";
import { MODAL_NAMES } from "constants/modalNames";
import ReservePromoBanner from "../shared/reserve-promo-banner";

class RentableFormFields extends React.Component {
  static propTypes = {
    browser: browserPropType,
    displayModal: PropTypes.func,
    errors: PropTypes.object,
    flagsAndExperiments: flagsAndExperimentsPropType,
    hasLengths: PropTypes.bool,
    isOneSize: PropTypes.bool.isRequired,
    isRentable: PropTypes.bool.isRequired,
    canRenderSplitTheBusiness: PropTypes.bool,
    logOpenAvailability: PropTypes.func.isRequired,
    offerBackupSize: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onChangeReservationInput: PropTypes.func,
    product: PropTypes.object.isRequired,
    remaining: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    reservation: PropTypes.object,
    selectedSku: PropTypes.shape({
      rentalAvailabilities: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
        })
      ),
    }),
    toggleMembershipLens: PropTypes.func.isRequired,
    updateReservation: PropTypes.func,
    userData: PropTypes.object,
  };

  static SIZE_AND_FIT_MODAL = "sizeAndFitModal";

  static INPUT_NAMES = {
    date: "reservation[date]",
    duration: "reservation[duration]",
    zip: "reservation[zipCode]",
  };

  state = {
    teaserModal: false,
  };

  componentDidMount() {
    if (getUserId(this.props.userData)) {
      this.fireKiffrExperimentData();
    }
  }

  componentDidUpdate(prevProps) {
    //Covers when a user logs in on the pdp
    if (!getUserId(prevProps?.userData) && getUserId(this.props.userData)) {
      this.fireKiffrExperimentData();
    }
  }

  fireKiffrExperimentData() {
    HeapHelpers.fireHeapEvent("kiffr-button-on-reserve-pdp-experiment", { userInExperiment: true });
  }

  showFitChart = e => {
    e.preventDefault();
    this.openTeaserModal(this.constructor.SIZE_AND_FIT_MODAL);

    ActionLogger.logAction({
      action: "click_sizeandfitnotes",
      objectType: "node",
      style_name: this.props.product.id,
    });
  };

  logReturnsModalAction = () => {
    ActionLogger.logAction({
      object_type: analytics.OBJECT_TYPE.RETURN_POLICY,
      action: analytics.ACTION_TYPE.CLICK,
    });
  };

  openReturnsModal = () => {
    this.props.displayModal(KeepItForeverReturnsModal.modalName);
    this.logReturnsModalAction();
  };

  openTeaserModal = modalName => {
    this.setState({ teaserModal: modalName });
  };

  hideTeaserModal = () => {
    this.setState({ teaserModal: false });
  };

  displayReservationCalendarModal = () => {
    this.props.displayModal(MODAL_NAMES.CALENDAR_DRAWER);
    this.props.logOpenAvailability();
  };

  shouldRenderSizeAndFit() {
    const { product } = this.props;
    const categoryId = product?.category?.id;
    // Add future categories here
    const ineligibleCategories = [merchandiseCategories.kids];
    // Don't show Size and Fit markup if categories do not have sizes and fit and if there is only a single size
    return !ineligibleCategories.includes(categoryId) && Object.keys(product.sizes ?? {}).length > 1;
  }

  sizeAndFit() {
    if (!this.shouldRenderSizeAndFit()) return;

    return (
      <div className="rentable-form-fields__size-and-fit-wrapper">
        <AtomLinkButton
          bold={false}
          className="rentable-form-fields__size-and-fit-wrapper__action"
          onClick={this.showFitChart}
          type="button">
          Size & Fit
        </AtomLinkButton>
      </div>
    );
  }

  zipCodeValue() {
    return this.props?.reservation?.zipCode ?? "";
  }

  renderSizeSelectors(inline) {
    return (
      <>
        <SizeSelectors
          isRental={isRental(this.props.product?.price)}
          isBulk={isBulk(this.props.product?.price)}
          isClearance={isClearance(this.props.product?.price)}
          sizeNames={this.props.product.sizeScale.sizeNames}
          skus={this.props.product.skus}
          onChange={this.props.onChange}
          onAnimatedInputChange={this.props.onChangeReservationInput}
          primarySkuValue={this.props?.reservation?.primarySku?.id}
          backupSkuValue={this.props?.reservation?.backupSku?.id}
          offerBackupSize={this.props.offerBackupSize}
          primarySkuError={this.props?.errors?.reservation?.primarySku}
          backupSkuError={this.props?.errors?.reservation?.backupSku}
          inline={inline}
          isMobileViewport={this.props.browser.isMobileViewport}
          placeholderText={ProductCollectionHelpers.getSizesPlaceholder(this.props.product, true)}
        />
        <UrgencySignalWrapper className="mt-2" selectedSize={this.props?.reservation?.primarySku?.id} />
      </>
    );
  }

  renderPDPSubmitActionsWrapper() {
    const { product, reservation } = this.props;
    return (
      <PDPSubmitActionsWrapper
        browser={this.props.browser}
        isExistingHold={Boolean(reservation?.primaryHoldId)}
        isOneSize={this.props.isOneSize}
        isRentable={this.props.isRentable}
        price={product?.price}
        productId={product?.id}
        remaining={this.props.remaining}
        selectedSku={this.props.selectedSku}
      />
    );
  }
  // redirect to plans page and log action for split the business experiment
  logSubscriptionCTA = plansPageUrl => {
    ActionLogger.inferAction({
      object_type: PDP_TYPE.RESERVE_PDP,
      action: analytics.ACTION_TYPE.CLICK_TO_PLANS,
      destination_url: plansPageUrl,
    });
  };

  renderReservationForm() {
    const { reservation, canRenderSplitTheBusiness, userData } = this.props;
    const isLoggedIn = !!getUserId(this.props.userData);
    return (
      <div>
        {!this.props.flagsAndExperiments?.[flagsAndExperimentNames.RSV_1494_ATB_TABS] ? <ReservePromoBanner /> : null}
        <DateZipInput
          date={reservation?.date}
          duration={reservation?.duration}
          zipCode={reservation?.zipCode}
          onOpenModal={this.displayReservationCalendarModal}
          errors={this.props.errors}
          dateInputName={this.constructor.INPUT_NAMES.date}
          durationInputName={this.constructor.INPUT_NAMES.duration}
          zipInputName={this.constructor.INPUT_NAMES.zip}
          updateReservation={this.props.updateReservation}
          onChangeReservationInput={this.props.onChangeReservationInput}
        />
        {this.renderSizeSelectors(true)}
        {this.sizeAndFit()}

        <FitGuarantee lens={MembershipHelpers.getUserDataLens(userData)} placement="pdp" />

        {this.renderPDPSubmitActionsWrapper()}
        {isLoggedIn && this.props.flagsAndExperiments?.[flagsAndExperimentNames.RSV_KIFFR_BUTTON_ON_RESERVE_PDP] && (
          <KiffrAtbButton
            preSelectedSku={this.props.reservation?.primarySku?.id}
            sizeNames={this.props.product.sizeScale.sizeNames}
            skus={this.props.product.skus}
            userData={userData}
            product={this.props.product}
          />
        )}
        {canRenderSplitTheBusiness && !this.props.flagsAndExperiments?.[flagsAndExperimentNames.RSV_1494_ATB_TABS] ? (
          <AtomLinkButton
            bold={false}
            className="subscription-browse-cta"
            role="link"
            onClick={this.props.toggleMembershipLens}>
            Try Subscription
          </AtomLinkButton>
        ) : null}
        {isLoggedIn && this.props.flagsAndExperiments?.[flagsAndExperimentNames.RSV_KIFFR_BUTTON_ON_RESERVE_PDP] && (
          <>
            <p className="kiffr_returns_copy">
              Returns for purchases accepted within 7 days, restocking fee applies.{" "}
              <button onClick={this.openReturnsModal}>Learn More.</button>
            </p>
          </>
        )}
      </div>
    );
  }

  render() {
    let modal, modalContent, modalClass;

    if (this.state.teaserModal) {
      if (this.state.teaserModal === this.constructor.SIZE_AND_FIT_MODAL) {
        modalContent = (
          <FitChart
            hasLengths={this.props.hasLengths}
            product={this.props.product}
            reservation={this.props.reservation}
          />
        );

        modalClass = "modal--fit-chart";
      }

      if (this.state.teaserModal === this.constructor.PRO_TEASER) {
        modalContent = (
          <ProTeaser
            handleClick={this.handleProTeaserClick}
            PdpStyleName={this.props.product.id}
            closeModal={this.hideTeaserModal}
          />
        );
      }

      if (modalContent) {
        modal = (
          <ClosableModal isOpen={true} onRequestClose={this.hideTeaserModal} optionalClass={modalClass}>
            {modalContent}
          </ClosableModal>
        );
      }
    }

    return (
      <div className="rentable-form-fields">
        {this.renderReservationForm()}
        {modal}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  cmsContent: state.smartComponentState?.PDPContent?.data,
  userData: state.userData,
});

const mapDispatchToProps = {
  toggleMembershipLens: () => ProductsActions.setMembershipLens(membershipLens.unlimited, false),
};

export { RentableFormFields as UnConnectedRentableFormFields };

export default compose(
  withOnLoadFlagsAndExperiments(
    flagsAndExperimentNames.RSV_KIFFR_BUTTON_ON_RESERVE_PDP,
    flagsAndExperimentNames.RSV_1494_ATB_TABS
  ),
  connect(mapStateToProps, mapDispatchToProps)
)(RentableFormFields);
